import axios from "axios";
import { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import {
  DASHBOARD,
  COUNTRY_LIST_DROPDOWN,
  STATE_LIST_DROPDOWN,
  REGION_LIST_DROPDOWN,
  CHAPTER_LIST_DROPDOWN,
} from "../../routes/routes";
import Select from "react-select";
import { hasRole } from "../../components/Utils";
import { usePermission } from "../../hooks/usePermission";
import moment from "moment";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/themes/material_red.css";
import { jsPDF } from "jspdf";
import "jspdf-autotable";
import { pdfHeaderStyles, selectFilterStyles } from "../../constant/theme";
import { toast } from "react-toastify";
import "./Dashboard.css";
import dashboardIcon from "../../assets/images/icons/dashboard.png";

// Dashboard widget components
import AnalyticsCards from "./components/AnalyticsCards";
import ChartSwiper from "./components/ChartSwiper";
import ChapterWiseCharts from "./components/ChapterWiseCharts";
import TopAchievers from "./components/TopAchievers";
import BirthdayCard from "./components/BirthdayCard";
import PendingListsTables from "./components/PendingListsTables";
import TopTenTables from "./components/TopTenTables";

const Dashboard = () => {
  const { hasPermission } = usePermission();
  const auth_user = JSON.parse(localStorage.getItem("user"));
  const isSuperAdmin = auth_user?.user_type === 1 ? 1 : 0;
  const isRegionAdmin = auth_user?.user_type === 2 ? 1 : 0;
  const isChapterAdmin = auth_user?.user_type === 3;

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [ovalLoading, setOvalLoading] = useState(true);
  const [countryOptions, setCountryOptions] = useState({});
  const [stateOptions, setStateOptions] = useState({});
  const [regionOptions, setRegionOptions] = useState([]);
  const [chapterOptions, setChapterOptions] = useState([]);
  const [filter, setFilter] = useState({
    country_id: null,
    state_id: null,
    region_id: null,
    // Only Chapter Admin auto-filters by their assigned chapter
    chapter_id: isChapterAdmin ? auth_user?.chapter_id || null : null,
  });
  const [showClearBtn, setShowClearBtn] = useState(false);
  const [showFiltersPanel, setShowFiltersPanel] = useState(false);
  const [filterPanelPos, setFilterPanelPos] = useState({ top: 0, right: 0 });
  const refFlatpickr = useRef(null);
  const filterPanelRef = useRef(null);
  const filterBtnRef = useRef(null);
  const [pdfLoading, setPdfLoading] = useState(false);

  const selectStateRef = useRef(null);
  const selectRegionRef = useRef(null);
  const selectChapterRef = useRef(null);

  // Count active location filters
  const activeFilterCount = [
    filter.country_id,
    filter.state_id,
    filter.region_id,
    filter.chapter_id,
  ].filter(Boolean).length;

  // Close filter panel on outside click
  const handleClickOutside = useCallback((e) => {
    if (
      filterPanelRef.current &&
      !filterPanelRef.current.contains(e.target) &&
      filterBtnRef.current &&
      !filterBtnRef.current.contains(e.target)
    ) {
      setShowFiltersPanel(false);
    }
  }, []);

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [handleClickOutside]);

  // Keep filter panel anchored under the Filters button when portaled to body
  useEffect(() => {
    if (!showFiltersPanel || !filterBtnRef.current) return;

    const updatePos = () => {
      const rect = filterBtnRef.current.getBoundingClientRect();
      setFilterPanelPos({
        top: rect.bottom + 8,
        right: Math.max(8, window.innerWidth - rect.right),
      });
    };

    updatePos();
    window.addEventListener("resize", updatePos);
    window.addEventListener("scroll", updatePos, true);
    return () => {
      window.removeEventListener("resize", updatePos);
      window.removeEventListener("scroll", updatePos, true);
    };
  }, [showFiltersPanel]);

  const openFiltersPanel = () => {
    if (filterBtnRef.current) {
      const rect = filterBtnRef.current.getBoundingClientRect();
      setFilterPanelPos({
        top: rect.bottom + 8,
        right: Math.max(8, window.innerWidth - rect.right),
      });
    }
    setShowFiltersPanel((prev) => !prev);
  };

  const handleStateClear = () => {
    if (selectStateRef.current) selectStateRef.current.clearValue();
    if (selectRegionRef.current) selectRegionRef.current.clearValue();
    if (selectChapterRef.current) selectChapterRef.current.clearValue();
  };

  const handleRegionClear = () => {
    if (selectRegionRef.current) selectRegionRef.current.clearValue();
    if (selectChapterRef.current) selectChapterRef.current.clearValue();
  };

  const handleChapterClear = () => {
    if (selectChapterRef.current) selectChapterRef.current.clearValue();
  };

  const handleDownloadPDF = async (reportType) => {
    try {
      setPdfLoading(true);
      // Create a new PDF document
      const currentDate = moment().format("DD-MM-YYYY");
      const doc = new jsPDF("landscape");
      var headers = [];
      var pdfData = [];
      var fileName = "";
      var pdfTitle = "";

      headers = ["Sl.No."];
      if (hasRole([1])) {
        headers.push("Country");
      }
      if (hasRole([1, 6])) {
        headers.push("State");
      }
      if (hasRole([1, 6, 7])) {
        headers.push("Region");
      }
      if (hasRole([1, 6, 7, 2])) {
        headers.push("Chapter");
      }
      headers.push("Member");

      if (reportType === "top_10_points_members") {
        if (
          !data.top10PointsUsersList ||
          data.top10PointsUsersList.length === 0
        ) {
          toast.error("No data available to export");
          return;
        }
        // Define table headers and data
        headers.push("Points");
        pdfData = data.top10PointsUsersList.map((data) => {
          let pData = [data.sno];
          if (hasRole([1])) {
            pData.push(data.country_name);
          }
          if (hasRole([1, 6])) {
            pData.push(data.state_name);
          }
          if (hasRole([1, 6, 7])) {
            pData.push(data.region_name);
          }
          if (hasRole([1, 6, 7, 2])) {
            pData.push(data.chapter_name);
          }
          pData.push(data.name);
          pData.push(data.points);
          return pData;
        });
        fileName = `Top10Points_${currentDate}.pdf`;
        pdfTitle = `Top 10 Members by Points`;
      } else if (reportType === "top_10_business_given_members") {
        if (
          !data.top10BusinessUsersList ||
          data.top10BusinessUsersList.length === 0
        ) {
          toast.error("No data available to export");
          return;
        }
        // Define table headers and data
        headers.push("Business Value");
        pdfData = data.top10BusinessUsersList.map((data) => {
          let pData = [data.sno];
          if (hasRole([1])) {
            pData.push(data.country_name);
          }
          if (hasRole([1, 6])) {
            pData.push(data.state_name);
          }
          if (hasRole([1, 6, 7])) {
            pData.push(data.region_name);
          }
          if (hasRole([1, 6, 7, 2])) {
            pData.push(data.chapter_name);
          }
          pData.push(data.name);
          pData.push(
            parseFloat(data.business_value_without_format).toLocaleString(
              "en-IN",
            ),
          );
          return pData;
        });
        fileName = `Top10Business_${currentDate}.pdf`;
        pdfTitle = `Top 10 Members by Business Value`;
      } else if (reportType === "top_10_referral_given_members") {
        if (
          !data.top10ReferralsGivenUsersList ||
          data.top10ReferralsGivenUsersList.length === 0
        ) {
          toast.error("No data available to export");
          return;
        }
        // Define table headers and data
        headers.push("RFLs Count");
        pdfData = data.top10ReferralsGivenUsersList.map((data) => {
          let pData = [data.sno];
          if (hasRole([1])) {
            pData.push(data.country_name);
          }
          if (hasRole([1, 6])) {
            pData.push(data.state_name);
          }
          if (hasRole([1, 6, 7])) {
            pData.push(data.region_name);
          }
          if (hasRole([1, 6, 7, 2])) {
            pData.push(data.chapter_name);
          }
          pData.push(data.name);
          pData.push(data.referrals_count);
          return pData;
        });
        fileName = `Top10Referrals_${currentDate}.pdf`;
        pdfTitle = `Top 10 Members by Referrals Given Count`;
      } else if (reportType === "top_10_visitors_members") {
        if (
          !data.top10VisitorsGivenUsersList ||
          data.top10VisitorsGivenUsersList.length === 0
        ) {
          toast.error("No data available to export");
          return;
        }
        // Define table headers and data
        headers.push("Visitors Count");
        pdfData = data.top10VisitorsGivenUsersList.map((data) => {
          let pData = [data.sno];
          if (hasRole([1])) {
            pData.push(data.country_name);
          }
          if (hasRole([1, 6])) {
            pData.push(data.state_name);
          }
          if (hasRole([1, 6, 7])) {
            pData.push(data.region_name);
          }
          if (hasRole([1, 6, 7, 2])) {
            pData.push(data.chapter_name);
          }
          pData.push(data.name);
          pData.push(data.visitors_count);
          return pData;
        });
        fileName = `Top10Visitors_${currentDate}.pdf`;
        pdfTitle = `Top 10 Members by Visitors Invited Count`;
      } else if (reportType === "top_10_121_members") {
        if (
          !data.top10121sGivenUsersList ||
          data.top10121sGivenUsersList.length === 0
        ) {
          toast.error("No data available to export");
          return;
        }
        // Define table headers and data
        headers.push("121s Count");
        pdfData = data.top10121sGivenUsersList.map((data) => {
          let pData = [data.sno];
          if (hasRole([1])) {
            pData.push(data.country_name);
          }
          if (hasRole([1, 6])) {
            pData.push(data.state_name);
          }
          if (hasRole([1, 6, 7])) {
            pData.push(data.region_name);
          }
          if (hasRole([1, 6, 7, 2])) {
            pData.push(data.chapter_name);
          }
          pData.push(data.name);
          pData.push(data.one2one_count);
          return pData;
        });
        fileName = `121s_${currentDate}.pdf`;
        pdfTitle = `Top 10 Members by 121s Given Count`;
      } else if (reportType === "top_10_inductions_members") {
        if (
          !data.top10InductionsGivenUsersList ||
          data.top10InductionsGivenUsersList.length === 0
        ) {
          toast.error("No data available to export");
          return;
        }
        // Define table headers and data
        headers.push("Inductions Count");
        pdfData = data.top10InductionsGivenUsersList.map((data) => {
          let pData = [data.sno];
          if (hasRole([1])) {
            pData.push(data.country_name);
          }
          if (hasRole([1, 6])) {
            pData.push(data.state_name);
          }
          if (hasRole([1, 6, 7])) {
            pData.push(data.region_name);
          }
          if (hasRole([1, 6, 7, 2])) {
            pData.push(data.chapter_name);
          }
          pData.push(data.name);
          pData.push(data.inductions_count);
          return pData;
        });
        fileName = `Top10Inductions_${currentDate}.pdf`;
        pdfTitle = `Top 10 Members by Inductions Given Count`;
      } else if (reportType === "top_10_chief_guests_members") {
        if (
          !data.top10ChiefGuestGivenUsersList ||
          data.top10ChiefGuestGivenUsersList.length === 0
        ) {
          toast.error("No data available to export");
          return;
        }
        // Define table headers and data
        headers.push("Chief Guests Count");
        pdfData = data.top10ChiefGuestGivenUsersList.map((data) => {
          let pData = [data.sno];
          if (hasRole([1])) {
            pData.push(data.country_name);
          }
          if (hasRole([1, 6])) {
            pData.push(data.state_name);
          }
          if (hasRole([1, 6, 7])) {
            pData.push(data.region_name);
          }
          if (hasRole([1, 6, 7, 2])) {
            pData.push(data.chapter_name);
          }
          pData.push(data.name);
          pData.push(data.chief_guests_count);
          return pData;
        });
        fileName = `Top10ChiefGuests_${currentDate}.pdf`;
        pdfTitle = `Top 10 Members by Chief Guests Count`;
      } else if (reportType === "top_10_power_dates_members") {
        if (
          !data.top10PowerDateGivenUsersList ||
          data.top10PowerDateGivenUsersList.length === 0
        ) {
          toast.error("No data available to export");
          return;
        }
        // Define table headers and data
        headers.push("Power Dates Count");
        pdfData = data.top10PowerDateGivenUsersList.map((data) => {
          let pData = [data.sno];
          if (hasRole([1])) {
            pData.push(data.country_name);
          }
          if (hasRole([1, 6])) {
            pData.push(data.state_name);
          }
          if (hasRole([1, 6, 7])) {
            pData.push(data.region_name);
          }
          if (hasRole([1, 6, 7, 2])) {
            pData.push(data.chapter_name);
          }
          pData.push(data.name);
          pData.push(data.power_dates_count);
          return pData;
        });
        fileName = `Top10PowerDates_${currentDate}.pdf`;
        pdfTitle = `Top 10 Members by Power Dates Count`;
      }

      //pdf title starts
      const textWidth =
        (doc.getStringUnitWidth(pdfTitle) * doc.internal.getFontSize()) /
        doc.internal.scaleFactor;
      const textOffset = (doc.internal.pageSize.width - textWidth) / 2;
      doc.text(pdfTitle, textOffset, 10); // Centered title at the top
      //pdf title ends

      // Add the table to the document
      doc.autoTable({
        head: [headers],
        body: pdfData,
        margin: { top: 20, right: 20, bottom: 20, left: 20 }, // Adjust margins
        startY: 20, // Start position of the table from top
        headerStyles: { ...pdfHeaderStyles }, // Change header color
        styles: { lineWidth: 0.1 }, // Set border width for table cells
      });
      // Save the PDF
      doc.save(fileName);
    } catch (error) {
      console.error("<<<ERRRROR>>>", error);
      console.error("Something Went Wrong", error.message);
      toast.error(error.message);
    } finally {
      setPdfLoading(false);
    }
  };

  const handleDateRangeClear = () => {
    setShowClearBtn(false);
    refFlatpickr.current.flatpickr.setDate([]);
    setFilter({
      ...filter,
      from_date: "",
      to_date: "",
    });
  };

  // Fetch location dropdown options (independent of dashboard data)
  useEffect(() => {
    const fetchDropdowns = async () => {
      try {
        const countryResponse = await axios.get(COUNTRY_LIST_DROPDOWN);
        setCountryOptions(countryResponse.data.data);

        const stateResponse = await axios.get(STATE_LIST_DROPDOWN, {
          params: filter.country_id ? { country_id: filter.country_id } : {},
        });
        setStateOptions(stateResponse.data.data);

        const regionResponse = await axios.get(REGION_LIST_DROPDOWN, {
          params: filter.region_id
            ? { region_id: filter.region_id }
            : filter.state_id
            ? { state_id: filter.state_id }
            : filter.country_id
            ? { country_id: filter.country_id }
            : {},
        });
        setRegionOptions(regionResponse.data.data);

        const chapterResponse = await axios.get(CHAPTER_LIST_DROPDOWN, {
          params: filter.region_id
            ? { region_id: filter.region_id }
            : filter.state_id
            ? { state_id: filter.state_id }
            : filter.country_id
            ? { country_id: filter.country_id }
            : {},
        });
        const fetchedChapterOptions = chapterResponse.data.data;
        setChapterOptions(fetchedChapterOptions);

        // Region Admin: default to first chapter when none selected
        if (isRegionAdmin && fetchedChapterOptions?.length > 0) {
          setFilter((prevFilter) => {
            if (!prevFilter.chapter_id) {
              return {
                ...prevFilter,
                chapter_id: fetchedChapterOptions[0].value,
              };
            }
            return prevFilter;
          });
        }
      } catch (error) {
        console.error("Error fetching dropdown data:", error);
      }
    };
    fetchDropdowns();
  }, [filter.country_id, filter.state_id, filter.region_id]);

  // Re-default first chapter for Region Admin when chapter filter is cleared
  useEffect(() => {
    if (!isRegionAdmin || filter.chapter_id || !chapterOptions?.length) return;
    setFilter((prev) => ({
      ...prev,
      chapter_id: chapterOptions[0].value,
    }));
  }, [filter.chapter_id, chapterOptions, isRegionAdmin]);

  // Fetch dashboard data only when chapter filter is ready for role
  useEffect(() => {
    const needsChapterFilter = isRegionAdmin || isChapterAdmin;
    if (needsChapterFilter && !filter.chapter_id) {
      return;
    }

    setOvalLoading(true);
    setLoading(true);
    axios
      .get(DASHBOARD, {
        params: {
          isAdmin: 1,
          ...filter,
        },
      })
      .then((response) => {
        if (response.status === 200 && response.data.success) {
          setData(response.data.data);
        }
      })
      .finally(() => {
        setOvalLoading(false);
        setLoading(false);
      });
  }, [filter]);

  const handleCalendarClick = () => {
    if (refFlatpickr.current) {
      refFlatpickr.current.flatpickr.open();
    }
  };

  const dateRangeText =
    filter.from_date && filter.to_date
      ? `(${moment(filter.from_date).format("MMM DD, YYYY")} - ${moment(filter.to_date).format("MMM DD, YYYY")})`
      : "";

  return (
    <div className="content-wrapper">
      <div className="flex-grow-1">
        <div className="container-fluid dp-wrapper px-0">
          {/* Analytics Welcome Header */}
          <div className="dashboard-analytics-header">
            <div className="analytics-header-left">
              <h1 className="analytics-title d-flex align-items-center">
                <img
                  src={dashboardIcon}
                  alt="Dashboard"
                  className="analytics-title-icon me-2"
                />
                Dashboard
              </h1>
              {/* <p className="analytics-subtitle">
                Track chapter performance, member engagement, referrals, and
                networking activities.
              </p> */}
            </div>
            <div className="analytics-header-right">
              {/* <div className="analytics-quick-filters">
                <button
                  className={`quick-filter-btn ${filter.quick_range === "7d" ? "active" : ""}`}
                  onClick={() => {
                    const to = moment().format("YYYY-MM-DD");
                    const from = moment()
                      .subtract(7, "days")
                      .format("YYYY-MM-DD");
                    setShowClearBtn(true);
                    if (refFlatpickr.current) {
                      refFlatpickr.current.flatpickr.setDate([from, to]);
                    }
                    setFilter({
                      ...filter,
                      from_date: from,
                      to_date: to,
                      quick_range: "7d",
                    });
                  }}
                >
                  7d
                </button>
                <button
                  className={`quick-filter-btn ${filter.quick_range === "30d" ? "active" : ""}`}
                  onClick={() => {
                    const to = moment().format("YYYY-MM-DD");
                    const from = moment()
                      .subtract(30, "days")
                      .format("YYYY-MM-DD");
                    setShowClearBtn(true);
                    if (refFlatpickr.current) {
                      refFlatpickr.current.flatpickr.setDate([from, to]);
                    }
                    setFilter({
                      ...filter,
                      from_date: from,
                      to_date: to,
                      quick_range: "30d",
                    });
                  }}
                >
                  30d
                </button>
                <button
                  className={`quick-filter-btn ${filter.quick_range === "6m" ? "active" : ""}`}
                  onClick={() => {
                    const to = moment().format("YYYY-MM-DD");
                    const from = moment()
                      .subtract(6, "months")
                      .format("YYYY-MM-DD");
                    setShowClearBtn(true);
                    if (refFlatpickr.current) {
                      refFlatpickr.current.flatpickr.setDate([from, to]);
                    }
                    setFilter({
                      ...filter,
                      from_date: from,
                      to_date: to,
                      quick_range: "6m",
                    });
                  }}
                >
                  6m
                </button>
                <button
                  className={`quick-filter-btn ${filter.quick_range === "1y" ? "active" : ""}`}
                  onClick={() => {
                    const to = moment().format("YYYY-MM-DD");
                    const from = moment()
                      .subtract(1, "year")
                      .format("YYYY-MM-DD");
                    setShowClearBtn(true);
                    if (refFlatpickr.current) {
                      refFlatpickr.current.flatpickr.setDate([from, to]);
                    }
                    setFilter({
                      ...filter,
                      from_date: from,
                      to_date: to,
                      quick_range: "1y",
                    });
                  }}
                >
                  1y
                </button>
              </div> */}

              {/* Date Range Picker */}
              <div className="analytics-date-display">
                <i className="bx bx-calendar" onClick={handleCalendarClick}></i>
                <span onClick={handleCalendarClick}>
                  {filter.from_date && filter.to_date
                    ? `${moment(filter.from_date).format("MMM DD, YYYY")} – ${moment(filter.to_date).format("MMM DD, YYYY")}`
                    : "Select Date Range"}
                </span>
                <div className="analytics-flatpickr-hidden">
                  <Flatpickr
                    ref={refFlatpickr}
                    className="form-control"
                    options={{
                      mode: "range",
                      dateFormat: "Y-m-d",
                      onChange: (selected) => {
                        setShowClearBtn(true);
                        if (
                          selected[0] !== undefined &&
                          selected[1] !== undefined &&
                          selected[1] !== ""
                        ) {
                          setFilter({
                            ...filter,
                            from_date: selected[0]
                              ? moment(selected[0]).format("YYYY-MM-DD")
                              : "",
                            to_date: selected[1]
                              ? moment(selected[1]).format("YYYY-MM-DD")
                              : "",
                            quick_range: "",
                          });
                        }
                      },
                      onClose: (selected) => {
                        if (!selected[1]) {
                          setShowClearBtn(false);
                        }
                      },
                    }}
                    placeholder="Date Range"
                  />
                </div>
                {filter.from_date && filter.to_date && (
                  <i
                    className="bx bx-x analytics-date-clear"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDateRangeClear();
                    }}
                  ></i>
                )}
              </div>

              {/* Filters Button — hidden for Chapter Admin (user_type 3) */}
              {!isChapterAdmin && (
              <div className="filters-btn-wrapper">
                <button
                  ref={filterBtnRef}
                  className={`filters-toggle-btn ${showFiltersPanel ? "active" : ""} ${activeFilterCount > 0 ? "has-filters" : ""}`}
                  onClick={openFiltersPanel}
                >
                  <i className="bx bx-filter-alt"></i>
                  <span>Filters</span>
                  {activeFilterCount > 0 && (
                    <span className="filters-badge">{activeFilterCount}</span>
                  )}
                </button>

                {showFiltersPanel &&
                  createPortal(
                    <>
                      <div
                        className="filters-backdrop"
                        onClick={() => setShowFiltersPanel(false)}
                      />
                      <div
                        className="filters-dropdown-panel filters-dropdown-panel--portal"
                        ref={filterPanelRef}
                        style={{
                          top: filterPanelPos.top,
                          right: filterPanelPos.right,
                        }}
                      >
                        <div className="filters-panel-header">
                          <span className="filters-panel-title">
                            Filter by Location
                          </span>
                          <div className="filters-panel-header-actions">
                            {activeFilterCount > 0 && (
                              <button
                                className="filters-clear-all-btn"
                                onClick={() => {
                                  handleStateClear();
                                  setFilter({
                                    ...filter,
                                    country_id: null,
                                    state_id: null,
                                    region_id: null,
                                    chapter_id: null,
                                  });
                                }}
                              >
                                Clear All
                              </button>
                            )}
                            <button
                              type="button"
                              className="filters-panel-close"
                              title="Close"
                              onClick={() => setShowFiltersPanel(false)}
                            >
                              <i className="fa-solid fa-xmark"></i>
                            </button>
                          </div>
                        </div>
                        <div className="filters-panel-body">
                          {hasRole([1]) && (
                            <div className="filter-field">
                              <label className="filter-field-label">Country</label>
                              <Select
                                menuPortalTarget={document.body}
                                styles={selectFilterStyles}
                                name="country_id"
                                isClearable
                                options={countryOptions}
                                placeholder="All Countries"
                                value={
                                  countryOptions && Array.isArray(countryOptions)
                                    ? countryOptions.find(
                                        (o) => o.value === filter.country_id,
                                      ) || null
                                    : null
                                }
                                onChange={(selected) => {
                                  handleStateClear();
                                  setFilter({
                                    ...filter,
                                    country_id: selected?.value,
                                    state_id: null,
                                    region_id: null,
                                    chapter_id: null,
                                  });
                                }}
                              />
                            </div>
                          )}

                          {hasRole([1, 6]) && (
                            <div className="filter-field">
                              <label className="filter-field-label">State</label>
                              <Select
                                menuPortalTarget={document.body}
                                styles={selectFilterStyles}
                                ref={selectStateRef}
                                name="state_id"
                                isClearable
                                options={stateOptions}
                                placeholder="All States"
                                value={
                                  stateOptions && Array.isArray(stateOptions)
                                    ? stateOptions.find(
                                        (o) => o.value === filter.state_id,
                                      ) || null
                                    : null
                                }
                                onChange={(selected) => {
                                  handleRegionClear();
                                  setFilter({
                                    ...filter,
                                    state_id: selected?.value,
                                    region_id: null,
                                    chapter_id: null,
                                  });
                                }}
                              />
                            </div>
                          )}

                          {hasRole([1, 6, 7]) && (
                            <div className="filter-field">
                              <label className="filter-field-label">Region</label>
                              <Select
                                menuPortalTarget={document.body}
                                styles={selectFilterStyles}
                                ref={selectRegionRef}
                                name="region_id"
                                isClearable
                                options={regionOptions}
                                placeholder="All Regions"
                                value={
                                  regionOptions && Array.isArray(regionOptions)
                                    ? regionOptions.find(
                                        (o) => o.value === filter.region_id,
                                      ) || null
                                    : null
                                }
                                onChange={(selected) => {
                                  handleChapterClear();
                                  setFilter({
                                    ...filter,
                                    region_id: selected?.value,
                                    chapter_id: null,
                                  });
                                }}
                              />
                            </div>
                          )}

                          {hasRole([1, 6, 7, 2]) && (
                            <div className="filter-field">
                              <label className="filter-field-label">Chapter</label>
                              <Select
                                menuPortalTarget={document.body}
                                styles={selectFilterStyles}
                                ref={selectChapterRef}
                                name="chapter_id"
                                isClearable
                                options={chapterOptions}
                                placeholder="All Chapters"
                                value={
                                  chapterOptions && Array.isArray(chapterOptions)
                                    ? chapterOptions.find(
                                        (o) => o.value === filter.chapter_id,
                                      ) || null
                                    : null
                                }
                                onChange={(selected) => {
                                  setFilter({
                                    ...filter,
                                    chapter_id: selected?.value,
                                  });
                                }}
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    </>,
                    document.body,
                  )}
              </div>
              )}
            </div>
          </div>
        </div>

        {/* Analytics Stat Cards */}
        <AnalyticsCards
          data={data}
          ovalLoading={ovalLoading}
          hasPermission={hasPermission}
        />

        
        {/* Top Achievers of the Week */}
        <TopAchievers
          data={data}
          ovalLoading={ovalLoading}
          hasPermission={hasPermission}
          filter={filter}
        />

        {/* Chart Swiper */}
        <ChartSwiper
          data={data}
          ovalLoading={ovalLoading}
          hasPermission={hasPermission}
          dateRangeText={dateRangeText}
          side={
            <>
              <PendingListsTables
                data={data}
                loading={loading}
                hasPermission={hasPermission}
              />
              <BirthdayCard data={data} hasPermission={hasPermission} />
            </>
          }
        />

        {/* Chapter-wise pie charts */}
        <ChapterWiseCharts
          data={data}
          ovalLoading={ovalLoading}
          hasPermission={hasPermission}
          dateRangeText={dateRangeText}
        />

      </div>

      {/* Top 10 Tables */}
      <TopTenTables
        data={data}
        loading={loading}
        hasPermission={hasPermission}
        pdfLoading={pdfLoading}
        handleDownloadPDF={handleDownloadPDF}
        dateRangeText={dateRangeText}
      />

      <div className="content-backdrop fade"></div>
    </div>
  );
};

export default Dashboard;
